import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { hasPositivePerpsBalance, useMarginBalance } from 'app/(trade)/queries/marginBalance';
import { BACKGROUND_MARGIN_POLL_INTERVAL_MS } from 'app/(trade)/queries/marginPolling';
import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import type { CreditHistoryEntry, GetCreditHistoryResponse } from 'models/reqres';
import type { TransferVo } from 'models/transfer';
import { useMarginEnabled } from 'queries/marginEnabled';
import { usePaymentsMetadataResponseQuery } from 'queries/paymentsMetadata';
import { usePortfolioCurrent } from 'queries/portfolioCurrent';
import config from 'utils/config';

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

interface GetDepositsResponse {
    deposits: TransferVo[] | null;
}

export const depositsKeys = {
    all: ['deposits'] as const,
    user: (userId: string) => [...depositsKeys.all, userId] as const,
};

export const creditHistoryKeys = {
    all: ['credit_history'] as const,
    user: (userId: string) => [...creditHistoryKeys.all, userId] as const,
};

const getDeposits = async (userId: string, signal?: AbortSignal): Promise<TransferVo[]> => {
    const response = await api.get<GetDepositsResponse>(`/users/${userId}/deposits`, { signal });
    return response.data.deposits ?? [];
};

const getCreditHistory = async (userId: string, signal?: AbortSignal): Promise<CreditHistoryEntry[]> => {
    const response = await api.get<GetCreditHistoryResponse>(`/users/${userId}/credit_history`, { signal });
    return response.data.credits ?? [];
};

export const useDepositsQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const userId = useAuthStore((s) => s.userId);
    const isMember = useAuthStore((s) => s.isLoggedInMember);
    const hasTransfersAccess = useAuthStore((state) => state.derived).hasTransfersAccess();

    return useQuery({
        queryKey: depositsKeys.user(userId ?? ''),
        queryFn: ({ signal }) => getDeposits(userId!, signal),
        enabled: enabled && !!userId && isMember && hasTransfersAccess,
    });
};

/**
 * Suspense variant — must only be mounted when the caller knows the user is a non-sub-user member,
 * since useSuspenseQuery can't be skipped via `enabled`/`skipToken`.
 */
export const useDepositsSuspenseQuery = () => {
    const userId = useAuthStore((s) => s.userId);

    return useSuspenseQuery({
        queryKey: depositsKeys.user(userId ?? ''),
        queryFn: ({ signal }) => getDeposits(userId!, signal),
    });
};

export const useCreditHistoryQuery = () => {
    const userId = useAuthStore((s) => s.userId);
    const isMember = useAuthStore((s) => s.isLoggedInMember);

    return useQuery({
        queryKey: creditHistoryKeys.user(userId ?? ''),
        queryFn: ({ signal }) => getCreditHistory(userId!, signal),
        enabled: !!userId && isMember,
    });
};

export const useHasFunds = (): { hasFunds: boolean; isLoaded: boolean } => {
    const isMember = useAuthStore((state) => state.isLoggedInMember);
    const hasTransfersAccess = useAuthStore((state) => state.derived).hasTransfersAccess();
    const shouldLoadDepositMetadata = isMember && hasTransfersAccess;

    const paymentsMetadata = usePaymentsMetadataResponseQuery({ enabled: shouldLoadDepositMetadata });
    const { data: credits, isFetched: creditsFetched } = useCreditHistoryQuery();
    const { data: portfolio, isFetched: portfolioFetched } = usePortfolioCurrent();

    const { data: marginEnabledData, isFetched: marginEnabledFetched } = useMarginEnabled();
    const isMarginEnabled = marginEnabledData?.enabled ?? false;

    const { data: marginBalance, isFetched: marginBalanceFetched } = useMarginBalance({
        enabled: isMarginEnabled,
        refetchInterval: BACKGROUND_MARGIN_POLL_INTERVAL_MS,
    });

    const hasAppliedDeposit = paymentsMetadata.data?.has_applied_deposit ?? false;
    const hasCredits = credits?.some((c) => c.status === 'applied' && c.amount_cents > 0) ?? false;
    const hasCash = portfolio ? !isNaN(portfolio.a) && portfolio.a > 0 : false;
    const hasMarginFunds = hasPositivePerpsBalance(marginBalance);

    const marginIsLoaded = !isMarginEnabled || marginBalanceFetched;
    const depositMetadataLoaded = !shouldLoadDepositMetadata || paymentsMetadata.isFetched;

    return {
        hasFunds: hasAppliedDeposit || hasCredits || hasCash || hasMarginFunds,
        isLoaded: depositMetadataLoaded && creditsFetched && portfolioFetched && marginEnabledFetched && marginIsLoaded,
    };
};
